import { useEffect, useState } from "react";
import moment from "moment";
import { Tag, Typography, Tooltip, Input, Button, Space, Popover, theme } from "antd";
import { CheckOutlined, SearchOutlined } from "@ant-design/icons";

import { FilterForm } from "./Form/filter";

const { Text } = Typography;
const { useToken } = theme;

export const STATUS_COLORS = {
  PENDING: "default",
  PROCESSING: "processing",
  COMPLETED: "success",
  FAILED: "error",
};

export const WalletInjectionQueueLayout = {
  content: {
    type: "div",
    children: [
      {
        type: "collapse",
        defaultActiveKey: [0],
        className: "mb-4",
        panels: [FilterForm],
      },
    ],
  },
};

const ColumnSearchTitle = ({ label, field, filterQuery, applyColFilter }) => {
  const { token } = useToken();
  const isActive = !!filterQuery[field];
  const [open, setOpen] = useState(false);
  const [inputVal, setInputVal] = useState(filterQuery[field] ?? "");

  useEffect(() => {
    setInputVal(filterQuery[field] ?? "");
  }, [filterQuery[field]]); // eslint-disable-line

  const handleSearch = () => {
    applyColFilter(field, inputVal || null);
    setOpen(false);
  };

  const handleReset = () => {
    setInputVal("");
    applyColFilter(field, null);
    setOpen(false);
  };

  const content = (
    <div style={{ width: 260 }}>
      <Input
        placeholder={`Search ${label}...`}
        value={inputVal}
        onChange={(e) => setInputVal(e.target.value)}
        onPressEnter={handleSearch}
        allowClear
        onClear={handleReset}
        style={{ marginBottom: 8 }}
      />
      <Space>
        <Button type="primary" icon={<SearchOutlined />} size="small" style={{ width: 100 }} onClick={handleSearch}>
          Search
        </Button>
        <Button size="small" style={{ width: 80 }} onClick={handleReset}>
          Reset
        </Button>
      </Space>
    </div>
  );

  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 6, whiteSpace: "nowrap" }}>
      {label}
      <Popover
        content={content}
        trigger="click"
        open={open}
        onOpenChange={setOpen}
        placement="bottomLeft"
      >
        <SearchOutlined
          style={{ color: isActive ? token.colorPrimary : token.colorTextTertiary, cursor: "pointer", fontSize: 12 }}
          onClick={(e) => e.stopPropagation()}
        />
      </Popover>
    </span>
  );
};

export const queueTableColumns = (filterQuery, applyColFilter) => [
  {
    title: "Reward ID",
    dataIndex: "reward_id",
    key: "reward_id",
    render: (val) => (
      <Text code style={{ fontSize: 12, whiteSpace: "nowrap" }}>{val}</Text>
    ),
  },
  {
    title: <ColumnSearchTitle label="Member ID" field="member_id" filterQuery={filterQuery} applyColFilter={applyColFilter} />,
    dataIndex: "member_id",
    key: "member_id",
    render: (val) => val || <Text type="secondary">—</Text>,
  },
  {
    title: <ColumnSearchTitle label="From Member" field="from_member_id" filterQuery={filterQuery} applyColFilter={applyColFilter} />,
    dataIndex: "from_member_id",
    key: "from_member_id",
    render: (val) => val || <Text type="secondary">—</Text>,
  },
  {
    title: "Reward Type",
    dataIndex: "reward_type_name",
    key: "reward_type_name",
    render: (val) => val ? <Tag>{val}</Tag> : <Text type="secondary">—</Text>,
  },
  {
    title: "Type",
    dataIndex: "transfer_type",
    key: "transfer_type",
    render: (val) => {
      const color = val === "reward" ? "blue" : val === "tax_deduction" ? "red" : "orange";
      return <Tag color={color}>{val}</Tag>;
    },
  },
  {
    title: "Amount",
    dataIndex: "amount",
    key: "amount",
    align: "right",
    render: (val) => val != null ? <Text>{Number(val).toFixed(2)}</Text> : <Text type="secondary">—</Text>,
  },
  {
    title: <ColumnSearchTitle label="Remarks" field="remarks" filterQuery={filterQuery} applyColFilter={applyColFilter} />,
    dataIndex: "remarks",
    key: "remarks",
    render: (val, row) => val || <Text type="secondary">—</Text>,
  },
  {
    title: "Status",
    dataIndex: "status",
    key: "status",
    render: (val) => <Tag color={STATUS_COLORS[val] || "default"}>{val}</Tag>,
  },
  {
    title: "Ready",
    dataIndex: "is_ready",
    key: "is_ready",
    render: (val) => val ? <CheckOutlined style={{ color: "#52c41a" }} /> : <Text type="secondary">—</Text>,
  },
  {
    title: "Back Order",
    dataIndex: "is_back_order",
    key: "is_back_order",
    render: (val, row) => {
      if (!val) return <Text type="secondary">—</Text>;
      const range = row.reward_batch_id ? `${row.reward_batch_id}_backorder` : "—";
      return (
        <Tooltip title={`Self-bill commission range: ${range}`}>
          <Tag color="purple">Back Order</Tag>
        </Tooltip>
      );
    },
  },
  {
    title: "Attempts",
    dataIndex: "attempts",
    key: "attempts",
    render: (val, row) => `${val} / ${row.max_attempts ?? 3}`,
  },
  {
    title: "Reason",
    dataIndex: "reason_deleted",
    key: "reason_deleted",
    render: (val, row) => {
      const text = val || row.error_message || null;
      if (!text) return <Text type="secondary">—</Text>;
      const isError = row.status === "FAILED" || row.status === "PENDING";
      return (
        <Tooltip title={text}>
          <Text type={isError ? "warning" : "danger"} ellipsis style={{ maxWidth: 160 }}>
            {text}
          </Text>
        </Tooltip>
      );
    },
  },
  {
    title: "Created Date",
    dataIndex: "created_at",
    key: "created_at",
    render: (val) => moment(val).format("YYYY-MM-DD HH:mm"),
  },
];
